import { TFunction } from 'i18next'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { BadgeColorKeys } from '@/components/ui/badge'
import { TableColumn } from '@/components/ui/table'
import { EMPTY_VALUE } from '@/constants'
import { getAssetLink } from '@/helpers/get-asset-link'
import {
  CommunicationStatus,
  CommunicationType,
  CommunicationTypeEnum,
  getRecipientStatusColor,
} from '@/models'
import { AnnouncementRecipientModel, AnnouncementRecipientStatus } from '@/models/features'
import { Building } from '@/reducers/types'

// Note: Ids of the BE `CommunicationGroup` table
const RECIPIENT_GROUP_ID = {
  current: 2,
  future: 1,
  past: 3,
  staff: 4,
} as const

const ANNOUNCEMENT_STATUS_COLOR_MAP: Record<CommunicationStatus, BadgeColorKeys> = {
  Completed: 'blue',
  Ongoing: 'green',
  Processing: 'orange',
  Scheduled: 'purple',
}

export const getAnnouncementStatusColor = (status: CommunicationStatus): BadgeColorKeys => {
  return ANNOUNCEMENT_STATUS_COLOR_MAP[status]
}

export { getRecipientStatusColor }

// Note: 'All' maps to empty string (no filter), other statuses are lowercased for the API
export const getRecipientStatusParam = (status?: string): string => {
  if (!status || status === 'All') return ''
  return status.toLowerCase()
}

export const getRecipientStatusLabel = (
  status: AnnouncementRecipientStatus,
  t: TFunction<['communication']>,
): string => {
  const RECIPIENT_STATUS_LABEL_MAP: Record<AnnouncementRecipientStatus, string> = {
    clicked: t('communication:announcementDetails.recipientsTable.status.clicked'),
    delivered: t('communication:announcementDetails.recipientsTable.status.delivered'),
    opened: t('communication:announcementDetails.recipientsTable.status.opened'),
    sent: t('communication:announcementDetails.recipientsTable.status.sent'),
  }

  return RECIPIENT_STATUS_LABEL_MAP[status]
}

// Note: Mirrors the BE `Utilities.buildingHasPMS`, which decides whether future residents are offered
const hasPmsIntegration = (building?: Building): boolean =>
  !!(
    building?.entrataPropertyId ||
    building?.yardiPropertyId ||
    building?.realPagePropertyId ||
    building?.rentManagerPropertyId
  )

// Note: The groups the form offered, mirroring the BE `findto*` controllers: SMS "All" is current +
// past residents, notifications leave out past residents and staff users, and future residents are
// only offered for PMS buildings
const getOfferedRecipientGroupIds = (type: CommunicationType, building?: Building): number[] => {
  const { current, future, past, staff } = RECIPIENT_GROUP_ID
  if (type === CommunicationTypeEnum.enum.SMS) return [current, past]

  const groupIds: number[] =
    type === CommunicationTypeEnum.enum.Notification ? [current] : [current, past, staff]
  return hasPmsIntegration(building) ? [future, ...groupIds] : groupIds
}

// Note: Both ids come from BE json columns, which are `null` when nothing was selected
type GetRecipientsLabelParams = {
  building?: Building
  recipientsCount: number
  standaloneClientIds?: number[] | null
  type: CommunicationType
  userGroupIds?: number[] | null
}

// Note: Selected groups are shown by name, and hand-picked residents turn it into the count. The
// email/notification "All residents" option selects every group plus the loaded residents, so that
// combination is "All Residents", the same as SMS "All".
export const getRecipientsLabel = (
  { building, recipientsCount, type, ...params }: GetRecipientsLabelParams,
  t: TFunction<['communication']>,
): string => {
  const standaloneClientIds = params.standaloneClientIds ?? []
  // Note: Old announcements can have the same group id repeated
  const userGroupIds = [...new Set(params.userGroupIds ?? [])]
  const countLabel = t('communication:announcementDetails.recipients', { recipientsCount })
  if (!userGroupIds.length) return countLabel

  // Note: The form used to send group ids as client ids too, so those aren't hand-picked residents
  const hasHandPickedResidents = standaloneClientIds.some((id) => !userGroupIds.includes(id))
  const isEveryOfferedGroupSelected = getOfferedRecipientGroupIds(type, building).every((id) =>
    userGroupIds.includes(id),
  )
  const isSms = type === CommunicationTypeEnum.enum.SMS
  if (isEveryOfferedGroupSelected && (isSms || hasHandPickedResidents)) {
    return t('communication:announcementDetails.recipientGroups.all')
  }
  if (hasHandPickedResidents) return countLabel

  const GROUP_LABEL_MAP: Record<number, string> = {
    [RECIPIENT_GROUP_ID.current]: t('communication:announcementDetails.recipientGroups.current'),
    [RECIPIENT_GROUP_ID.future]: t('communication:announcementDetails.recipientGroups.future'),
    [RECIPIENT_GROUP_ID.past]: t('communication:announcementDetails.recipientGroups.past'),
    [RECIPIENT_GROUP_ID.staff]: t('communication:announcementDetails.recipientGroups.staff'),
  }
  const groupLabels = userGroupIds.map((id) => GROUP_LABEL_MAP[id]).filter(Boolean)

  return groupLabels.length ? groupLabels.join(', ') : countLabel
}

// Note: A duplicate is a new announcement, so the BE only takes attachments as uploaded files
export const fetchAttachmentFiles = (attachments: { fileName: string }[]): Promise<File[]> =>
  Promise.all(
    attachments.map(async ({ fileName }) => {
      const response = await fetch(
        getAssetLink({ file: fileName, path: 'chitown-images/notification' }),
      )
      if (!response.ok) throw new Error(`Could not load attachment ${fileName}`)
      return new File([await response.blob()], fileName, {
        type: response.headers.get('content-type') ?? '',
      })
    }),
  )

// Note: the Resident column shared by the notification / email / SMS recipient tables - one
// "Column data settings" entry (Announcements → resident) drives all three.
export const getAnnouncementResidentColumn = (
  t: TFunction<['communication']>,
  settings: PersonColumnSettings,
): TableColumn<AnnouncementRecipientModel> =>
  getPersonColumn(settings, {
    getDisplay: (row) => row.residentDisplay,
    getPerson: (recipient) => ({
      apartment: recipient.unitNumber === EMPTY_VALUE ? null : recipient.unitNumber,
      avatar: recipient.avatar,
      buildingNumber: recipient.buildingNumber,
      clientId: recipient.residentId,
      email: recipient.email,
      firstName: recipient.firstName,
      kind: 'resident',
      lastName: recipient.lastName,
      phone: recipient.phone,
      street: recipient.street,
    }),
    id: 'resident',
    pageName: t('communication:announcementDetails.recipientsTable.pageName'),
    source: 'announcements',
    title: t('communication:announcementDetails.recipientsTable.columns.resident'),
  })
